import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPostsByTag } from '../services/blogService';
import type { BlogPost } from '../types';
import { updateMetaTag, updateLinkTag, updateRobotsTag } from '../hooks/useSeo';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const BlogTagPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      if (tag) {
        const fetchedPosts = await getBlogPostsByTag(tag);
        setPosts(fetchedPosts);
      }
      setLoading(false);
    };
    fetchPosts();
  }, [tag]);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('app-rendered'));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, posts]);

  const decodedTag = tag ? decodeURIComponent(tag) : '';

  useIsomorphicLayoutEffect(() => {
    document.title = `Posts tagged with "${decodedTag}" - BrandGenius`;
    updateMetaTag('name', 'description', `Articles and insights tagged with ${decodedTag}.`);
    updateLinkTag('canonical', `https://www.brandgenius.app/blog/tag/${tag}/`);
    updateRobotsTag(true); // Since it was "noindex, follow" in helmet
  }, [decodedTag, tag]);

  return (
    <>
      <div className="w-full max-w-6xl mx-auto py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
        <div className="w-full max-w-3xl mx-auto mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-6">
            Tag: <span className="text-indigo-600">#{decodedTag}</span>
          </h1>
          <Link to="/blog/" className="text-indigo-600 hover:text-indigo-800 font-medium">&larr; Back to all posts</Link>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No blog posts found for this tag.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map(post => (
              <div key={post.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow duration-300">
                {post.featuredImage && (
                  <Link to={`/blog/${post.slug}/`}>
                    <img src={post.featuredImage} alt={post.title} className="w-full h-48 object-cover" />
                  </Link>
                )}
                <div className="p-6 flex flex-col flex-grow">
                  <Link to={`/blog/${post.slug}/`} className="block mb-2 mt-2">
                    <h2 className="text-xl font-bold text-gray-900 hover:text-indigo-600 transition-colors line-clamp-2">{post.title}</h2>
                  </Link>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between text-xs text-gray-500">
                    <span>{post.author}</span>
                    <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
